import React, { ChangeEventHandler, useMemo, useRef, useState } from 'react';
import cls from 'classnames';
import styles from './Fields.module.scss';
import { isString } from '@/utils/is';

export type FieldValue = number | string;

interface TextFieldProps {
  placeholder?: string;
  onChange?(value: FieldValue): void;
  onBlur?(): void;
  value?: FieldValue;
  isError?: boolean;
}

const Input: React.FC<TextFieldProps> = ({
  onChange,
  value,
  isError,
  ...restProps
}) => {
  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    onChange && onChange(value);
  };

  return (
    <input
      className={cls(styles.input, { [styles.input__error]: isError })}
      type="text"
      value={value || ''}
      onChange={handleChange}
      {...restProps}
    />
  );
};

const Textarea: React.FC<TextFieldProps> = ({
  onChange,
  value,
  isError,
  ...restProps
}) => {
  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const value = e.target.value;
    onChange && onChange(value);
  };

  return (
    <textarea
      className={cls(styles.input, styles.textarea, {
        [styles.input__error]: isError,
      })}
      value={value || ''}
      onChange={handleChange}
      {...restProps}
    />
  );
};

interface SelectOption {
  label: string;
  value: FieldValue;
}

interface SelectProps extends TextFieldProps {
  options: (SelectOption | string)[];
  allowSearch?: boolean;
}

const Select: React.FC<SelectProps> = ({
  options: propsOptions,
  onChange,
  value,
  isError,
  onBlur,
  allowSearch = false,
  ...restProps
}) => {
  const { placeholder } = restProps;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const isHoverOptsRef = useRef<boolean>(false);

  const options = useMemo(
    () =>
      propsOptions
        .map((item) => (isString(item) ? { label: item, value: item } : item))
        .filter((item) =>
          item.label.toLowerCase().includes((searchValue || '').toLowerCase())
        ),
    [searchValue, propsOptions]
  );

  const curLabel = useMemo(() => {
    const option = options.find((item) => item.value === value);
    return option ? option.label : '';
  }, [value, options]);

  const handleToggleIsOpen = (newOpen: boolean) => {
    if (isHoverOptsRef.current) {
      return;
    }
    if (allowSearch && newOpen) {
      setSearchValue(curLabel || null);
    } else {
      setSearchValue(null);
    }
    setIsOpen(newOpen);
  };

  const handleBlur = () => {
    handleToggleIsOpen(false);
    onBlur && onBlur();
  };

  const handleChange = (value: string | number) => {
    isHoverOptsRef.current = false;
    onChange && onChange(value);
    handleBlur();
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (allowSearch) {
      const value = e.target.value;
      setSearchValue(value);
    }
  };

  return (
    <div className={styles.select__container}>
      <input
        value={searchValue === null ? curLabel : searchValue}
        className={cls(styles.input, styles.select, {
          [styles.input__error]: isError,
        })}
        readOnly={!allowSearch}
        onFocus={handleToggleIsOpen.bind(null, !isOpen)}
        onBlur={handleBlur}
        onChange={handleSearch}
        {...restProps}
        placeholder={curLabel || placeholder}
      />

      {isOpen && (
        <ul className={styles.select__options}>
          {options?.map(({ label, value }) => (
            <li
              key={value}
              className={styles.select__options__item}
              onClick={handleChange.bind(null, value)}
              onMouseOver={() => {
                isHoverOptsRef.current = true;
              }}
              onMouseLeave={() => {
                isHoverOptsRef.current = false;
              }}
            >
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const fields = {
  input: Input,
  textarea: Textarea,
  select: Select,
};

export type FieldType = keyof typeof fields;

export interface FieldsProps {
  type: FieldType;
  onChange?(value: FieldValue): void;
  value?: FieldValue;
  name?: string;
  isError?: boolean;
}

const Fields: React.FC<FieldsProps> = ({ type, ...restProps }) => {
  const filed = fields[type];
  return React.createElement(filed, restProps as Record<string, unknown>);
};

export default Fields;
